import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HiOutlineBell, HiOutlineChevronDown, HiOutlineUser, HiOutlineCog, HiOutlineLogout } from 'react-icons/hi';
import { notificationService } from '../../services/notifications';
import { useAuth } from '../../hooks/useAuth';

const ROUTE_TITLES = {
  '/dashboard': 'Dashboard',
  '/organizations': 'Organizations',
  '/employees': 'Employees',
  '/documents': 'Documents',
  '/approvals': 'Approvals',
  '/notifications': 'Notifications',
  '/activity-logs': 'Activity Logs',
  '/settings': 'Settings',
  '/profile': 'Profile',
};

function getPageTitle(pathname) {
  const match = Object.keys(ROUTE_TITLES).find((key) => pathname.startsWith(key));
  if (match) return ROUTE_TITLES[match];
  return 'MMPR';
}

export default function Header() {
  const location = useLocation();
  const { user, logout, currentOrganization, setOrganization } = useAuth();
  const queryClient = useQueryClient();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const orgMenuRef = useRef(null);

  const pageTitle = getPageTitle(location.pathname);

  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationService.unreadCount(),
    refetchInterval: 30000,
    select: (res) => res.data?.count || 0,
  });

  const unreadCount = unreadData || 0;
  const organizations = user?.organizations || [];
  const currentOrg = organizations.find((o) => String(o.id) === String(currentOrganization));

  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (orgMenuRef.current && !orgMenuRef.current.contains(e.target)) {
        setOrgMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || user.email?.[0]?.toUpperCase()
    : '?';

  return (
    <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-secondary-200 bg-white px-6">
      {/* Page title */}
      <div>
        <h2 className="text-lg font-semibold text-secondary-900">{pageTitle}</h2>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Organization switcher — shown for any role that has multiple org assignments.
            The `organizations` array is only populated by the backend for multi-org roles
            (currently AREA_MANAGER), so checking length > 1 is scope-agnostic. */}
        {organizations.length > 1 && (
          <div className="relative" ref={orgMenuRef}>
            <button
              onClick={() => setOrgMenuOpen(!orgMenuOpen)}
              className="flex items-center gap-2 rounded-lg border border-secondary-200 px-3 py-1.5 text-sm text-secondary-700 transition-colors hover:bg-secondary-50"
            >
              <span className="max-w-[160px] truncate">{currentOrg?.name || 'Select Org'}</span>
              <HiOutlineChevronDown className="h-4 w-4 text-secondary-400" />
            </button>

            {orgMenuOpen && (
              <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-secondary-200 bg-white py-1 shadow-lg">
                {organizations.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => {
                      setOrganization(String(org.id));
                      queryClient.invalidateQueries();
                      setOrgMenuOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors hover:bg-secondary-50 ${String(org.id) === String(currentOrganization)
                        ? 'bg-primary-50 font-medium text-primary-700'
                        : 'text-secondary-700'
                      }`}
                  >
                    {org.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notifications */}
        <Link
          to="/notifications"
          className="relative rounded-lg p-2 text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-secondary-700"
        >
          <HiOutlineBell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-secondary-100"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
              {initials}
            </div>
            <HiOutlineChevronDown className="h-3.5 w-3.5 text-secondary-400" />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-secondary-200 bg-white py-1 shadow-lg">
              <div className="border-b border-secondary-100 px-4 py-3">
                <p className="text-sm font-medium text-secondary-900">
                  {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'User'}
                </p>
                <p className="text-xs text-secondary-500">{user?.email}</p>
              </div>
              <Link
                to="/profile"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-secondary-700 transition-colors hover:bg-secondary-50"
              >
                <HiOutlineUser className="h-4 w-4 text-secondary-400" />
                Profile
              </Link>
              <Link
                to="/settings"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-secondary-700 transition-colors hover:bg-secondary-50"
              >
                <HiOutlineCog className="h-4 w-4 text-secondary-400" />
                Settings
              </Link>
              <div className="border-t border-secondary-100">
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                  }}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-danger-500 transition-colors hover:bg-danger-50"
                >
                  <HiOutlineLogout className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
